import { AuthAdminGuard, LoginAdminGuard } from '@/middlewares/AuthAdminMiddleware'
import { AuthGuard, ForgotPasswordGuard, LoginGuard } from '@/middlewares/AuthMiddleware'
import { AuthAffiliateGuard, LoginAffiliateGuard, LojaAffiliateGuard } from '@/middlewares/AffiliatePortalMiddleare'
import { CreateMotherOwnerGuard } from '@/middlewares/OwnersAdminMiddleware'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthAdminStore } from '@/stores/AuthAdminStore'

const routes = [
  {
    path: '/teste',
    name: 'teste',
    component: () => import('../views/admin/test.vue'),
  },
  // ===== Admin =====
  {
    path: '/admin',
    redirect: '/admin/home',
    meta: { ambient: 'admin', isAffiliate: false },
    component: () => import('../layouts/AdminLayout.vue'),
    beforeEnter: AuthAdminGuard,
    children: [
      {
        path: 'home',
        name: 'admin-home',
        meta: { requiresAuth: true },
        component: () => import('../views/admin/Home.vue'),
      },
      {
        path: 'agenda',
        name: 'admin-agenda',
        meta: { requiresAuth: true, keepAlive: true, permissions: { modulo: 'agenda', submodulo: '_', permissao: 'can_view' } },
        component: () => import('../views/admin/Agenda.vue'),
      },
      {
        path: 'filial',
        name: 'admin-filial',
        redirect: '/admin/filial/listar',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'listar',
            name: 'admin-filial-listar',
            meta: { permissions: { modulo: 'filial', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/filial/List.vue'),
          },
          {
            path: 'criar',
            name: 'admin-filial-criar',
            meta: { permissions: { modulo: 'filial', submodulo: '_', permissao: 'can_create' } },
            component: () => import('../views/admin/filial/Form.vue'),
          },
          {
            path: 'editar/:uuid',
            name: 'admin-filial-editar',
            meta: { permissions: { modulo: 'filial', submodulo: '_', permissao: 'can_update' } },
            component: () => import('../views/admin/filial/Form.vue'),
          },
          {
            path: 'caixas',
            name: 'admin-filial-caixas',
            meta: { permissions: { modulo: 'filial', submodulo: 'caixa', permissao: 'can_view' } },
            component: () => import('../views/admin/filial/CaixasView.vue'),
          }
        ],
      },
      {
        path: 'financeiro',
        name: 'admin-financeiro',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'contas',
            name: 'admin-financeiro-contas',
            meta: { permissions: { modulo: 'financeiro', submodulo: 'contas', permissao: 'can_view' } },
            component: () => import('../views/admin/financeiro/ContasView.vue'),
          },
          {
            path: 'ofx',
            name: 'admin-financeiro-ofx',
            meta: { permissions: { modulo: 'financeiro', submodulo: 'extrato_bancario', permissao: 'can_view' } },
            component: () => import('../views/admin/financeiro/OfxConciliacao.vue'),
          },
          {
            path: 'notas-fiscais',
            name: 'admin-financeiro-notas-fiscais',
            meta: { permissions: { modulo: 'financeiro', submodulo: 'contas', permissao: 'can_view' } },
            component: () => import('../views/admin/financeiro/NotasFiscaisView.vue'),
          },
          {
            path: 'importar-faturas',
            name: 'admin-financeiro-importar-faturas',
            meta: { requiresAuth: true, permissions: { modulo: 'financeiro', submodulo: 'faturas', permissao: 'can_view' } },
            component: () => import('../views/admin/financeiro/ImportarFaturasView.vue'),
          },
          {
            path: 'extratos-afiliados',
            name: 'admin-financeiro-extratos-afiliados',
            meta: { permissions: { modulo: 'afiliados', submodulo: 'comissao', permissao: 'can_view' } },
            component: () => import('../views/admin/financeiro/ExtratosAfiliadosView.vue'),
          },
          {
            path: 'fluxo-caixa',
            name: 'admin-financeiro-fluxo-caixa',
            meta: { permissions: { modulo: 'financeiro', submodulo: 'extrato_bancario', permissao: 'fluxo_de_caixa' } },
            component: () => import('../views/admin/financeiro/FluxoCaixaView.vue'),
          },
        ]
      },
      {
        path: 'produto',
        name: 'admin-produto',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'admin-produto-listar',
            meta: { permissions: { modulo: 'produtos', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/produto/List.vue'),
          },
        ],
      },
      {
        path: 'estoque',
        name: 'admin-estoque',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'controle',
            name: 'admin-estoque-controle',
            redirect: '/admin/estoque/controle/estoques',
            component: () => import('../layouts/estoque/EstoqueLayout.vue'),
            children: [
              {
                path: 'estoques',
                name: 'admin-estoque-controle-estoques',
                meta: { permissions: { modulo: 'estoque', submodulo: '_', permissao: 'can_view' } },
                component: () => import('../views/admin/estoque/EstoquesView.vue'),
              },
              {
                path: 'entrada-estoque',
                name: 'admin-estoque-controle-entrada',
                meta: { permissions: { modulo: 'estoque', submodulo: '_', permissao: 'can_manage_entrada_estoque' } },
                component: () => import('../views/admin/estoque/EntradaEstoqueView.vue'),
              },
            ]
          },
          {
            path: 'insumos',
            name: 'admin-estoque-insumos',
            meta: { permissions: { modulo: 'estoque', submodulo: '_', permissao: 'can_manage_insumos' } },
            component: () => import('../views/admin/estoque/InsumosView.vue'),
          },
        ],
      },
      {
        path: 'promocoes',
        name: 'admin-promocoes',
        meta: { requiresAuth: true, permissions: { modulo: 'produtos', submodulo: 'promocoes', permissao: 'can_view' } },
        component: () => import('../views/admin/produto/PromocoesView.vue'),
      },
      {
        path: 'cupons',
        name: 'admin-cupons',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'admin-cupons-listar',
            meta: { permissions: { modulo: 'produtos', submodulo: 'cupons', permissao: 'can_view' } },
            component: () => import('../views/admin/cupom/List.vue'),
          },
          {
            path: 'criar',
            name: 'admin-cupons-criar',
            meta: { permissions: { modulo: 'produtos', submodulo: 'cupons', permissao: 'can_create' } },
            component: () => import('../views/admin/cupom/Form.vue'),
          },
          {
            path: 'editar/:uuid',
            name: 'admin-cupons-editar',
            meta: { permissions: { modulo: 'produtos', submodulo: 'cupons', permissao: 'can_update' } },
            component: () => import('../views/admin/cupom/Form.vue'),
            props:true
          }
        ]
      },
      {
        path: 'feriado',
        name: 'admin-feriado',
        meta: { requiresAuth: true },
        component: () => import('../layouts/FeriadoLayout.vue'),
        children: [
          {
            path: '',
            name: 'admin-feriado-listar',
            meta: { requiresAuth: true, permissions: { modulo: 'filial', submodulo: 'feriados', permissao: 'can_view' } },
            component: () => import('../views/admin/feriado/List.vue'),
          },
          {
            path: 'criar',
            name: 'admin-feriado-criar',
            meta: { requiresAuth: true, permissions: { modulo: 'filial', submodulo: 'feriados', permissao: 'can_create' } },
            component: () => import('../views/admin/feriado/Form.vue'),
          },
          {
            path: 'editar/:uuid',
            name: 'admin-feriado-editar',
            meta: { requiresAuth: true, permissions: { modulo: 'filial', submodulo: 'feriados', permissao: 'can_update' } },
            component: () => import('../views/admin/feriado/Form.vue'),
          },
          {
            path: 'nacional',
            name: 'admin-feriado-nacional',
            meta: { requiresAuth: true, permissions: { modulo: 'filial', submodulo: 'feriados', permissao: 'can_view' } },
            component: () => import('../views/admin/feriado/FeriadoNacional.vue'),
          }
        ]
      },
      {
        path: 'carrinho',
        name: 'admin-carrinho',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'admin-carrinho-listar',
            meta: { requiresAuth: true, permissions: { modulo: 'carrinho', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/carrinho/List.vue'),
          },
          {
            path: 'trocar-produto/:uuid_carrinho_item',
            name: 'admin-carrinho-trocar-produto',
            meta: { requiresAuth: true,  permissions: { modulo: 'carrinho', submodulo: '_', permissao: 'trocar_produto' } },
            component: () => import('../views/admin/carrinho/TrocarProduto.vue'),
          }
        ]
      },
      {
        path: 'atendimento',
        name: 'admin-atendimento',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'visualizar/:uuid',
            name: 'admin-atendimento-visualizar',
            meta: { requiresAuth: true, permissions: { modulo: 'atendimento', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/atendimento/View.vue'),
          },
          {
            path: 'listar',
            name: 'admin-atendimento-listar',
            meta: { requiresAuth: true, permissions: { modulo: 'atendimento', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/atendimento/List.vue'),
          },
          {
            path: 'recuperacao',
            name: 'admin-atendimento-recuperacao',
            meta: { requiresAuth: true, permissions: { modulo: 'atendimento', submodulo: 'recuperacao', permissao: 'can_view' } },
            component: () => import('../views/admin/atendimento/Recuperacao.vue'),
          },
        ]
      },
      {
        path: 'afiliado',
        redirect: '/admin/afiliado/listar',
        name: 'admin-afiliado',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'listar',
            name: 'admin-afiliado-listar',
            meta: { requiresAuth: true, filtrosNaUrl: true, permissions: { modulo: 'afiliados', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/afiliado/List.vue'),
          },
          {
            path: 'comissao/:afiliado_uuid',
            name: 'admin-afiliado-comissao',
            meta: { requiresAuth: true, permissions: { modulo: 'afiliados', submodulo: 'comissao', permissao: 'can_view' } },
            component: () => import('../views/admin/afiliado/Comissao.vue'),
          },
          {
            path: 'contatos',
            name: 'admin-afiliado-contatos',
            meta: { requiresAuth: true, permissions: { modulo: 'afiliados', submodulo: 'contato', permissao: 'can_view' } },
            component: () => import('../views/admin/afiliado/Contatos.vue'),
          },
          {
            path: 'comunicados',
            name: 'admin-afiliado-comunicados',
            meta: { requiresAuth: true, permissions: { modulo: 'afiliados', submodulo: 'comunicado', permissao: 'can_view' } },
            component: () => import('../views/admin/afiliado/Comunicados.vue'),
          }
        ]
      },
      {
        path: 'configuracoes',
        name: 'admin-configuracoes',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'financeiro',
            name: 'admin-configuracoes-financeiro',
            component: () => import('../layouts/configuracoes/FinanceiroLayout.vue'),
            meta: { requiresAuth: true },
            children: [
              {
                path: '',
                name: 'admin-configuracoes-financeiro-index',
                component: () => import('../views/admin/configuracoes/financeiro/Index.vue'),
              },
              {
                path: 'categoria',
                name: 'admin-configuracoes-financeiro-categoria',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'financeiro', permissao: 'can_manage_categoria_conta' } },
                component: () => import('../views/admin/configuracoes/financeiro/Categoria.vue'),
              },
              {
                path: 'centro-de-custo',
                name: 'admin-configuracoes-financeiro-centro-de-custo',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'financeiro', permissao: 'can_manage_centro_de_custo' } },
                component: () => import('../views/admin/configuracoes/financeiro/CentroDeCusto.vue'),
              },
              {
                path: 'formas-de-pagamento',
                name: 'admin-configuracoes-financeiro-formas-de-pagamento',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'financeiro', permissao: 'can_manage_forma_pagamento' } },
                component: () => import('../views/admin/configuracoes/financeiro/FormaPagamento.vue'),
              },
              {
                path: 'tomador',
                name: 'admin-configuracoes-financeiro-tomador',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'financeiro', permissao: 'can_manage_tomador' } },
                component: () => import('../views/admin/configuracoes/financeiro/TomadorView.vue'),
              },
              {
                path: 'bancos',
                name: 'admin-configuracoes-financeiro-bancos',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'financeiro', permissao: 'can_manage_banco' } },
                component: () => import('../views/admin/configuracoes/financeiro/BancosView.vue'),
              }
            ]
          },
          {
            path: 'sistema',
            name: 'admin-configuracoes-sistema',
            redirect: '/admin/configuracoes/sistema/empresa',
            component: () => import('../layouts/configuracoes/SistemaLayout.vue'),
            meta: { requiresAuth: true },
            children: [
              {
                path: 'empresa',
                name: 'admin-configuracoes-sistema-empresa',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_update_empresa' } },
                component: () => import('../views/admin/configuracoes/sistema/EmpresaView.vue'),
              },
              {
                path: 'acesso',
                name: 'admin-configuracoes-sistema-acesso',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_acessos' } },
                component: () => import('../views/admin/configuracoes/sistema/AcessosView.vue'),
              },
              {
                path: 'cliente',
                name: 'admin-configuracoes-sistema-cliente',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_acessos' } },
                component: () => import('../views/admin/configuracoes/sistema/ClientesView.vue'),
              },
              {
                path: 'perfil',
                name: 'admin-configuracoes-sistema-perfil',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_perfil' } },
                component: () => import('../views/admin/configuracoes/sistema/PerfilView.vue'),
              },
              {
                path: 'templates/email',
                name: 'admin-configuracoes-sistema-templates-email',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_ecommerce' } },
                component: () => import('../views/admin/configuracoes/sistema/TemplateEmail.vue'),
              },
              {
                path: 'templates/whatsapp',
                name: 'admin-configuracoes-sistema-templates-whatsapp',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_ecommerce' } },
                component: () => import('../views/admin/configuracoes/sistema/TemplateWhatsapp.vue'),
              },
              {
                path: 'ecommerce',
                name: 'admin-configuracoes-sistema-ecommerce',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'sistemicas', permissao: 'can_manage_ecommerce' } },
                component: () => import('../views/admin/configuracoes/sistema/EcommerceView.vue'),
              },
            ]
          },
          {
            path: 'gateways',
            name: 'admin-configuracoes-gateways',
            redirect: '/admin/configuracoes/gateways/pagamento',
            component: () => import('@/layouts/configuracoes/GatewaysLayout.vue'),
            meta: { requiresAuth: true },
            children: [
              {
                path: 'pagamento',
                name: 'admin-configuracoes-gateways-pagamento',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_pagamento' } },
                component: () => import('@/views/admin/configuracoes/gateways/PaymentGateways.vue'),
              },
              {
                path: 'protocolo',
                name: 'admin-configuracoes-gateways-protocolo',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_protocolo' } },
                redirect: '/admin/configuracoes/gateways/protocolo',
                children: [
                  {
                    path: '',
                    name: 'admin-configuracoes-gateways-protocolo-config',
                    meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_protocolo' } },
                    component: () => import('@/views/admin/configuracoes/gateways/ProtocolGateways.vue'),
                  },
                  {
                    path: 'importacao',
                    name: 'admin-configuracoes-gateways-protocolo-importacao',
                    meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_protocolo' } },
                    component: () => import('@/views/admin/configuracoes/gateways/ImportProtocol.vue'),
                  },
                ]
              },
              {
                path: 'email',
                name: 'admin-configuracoes-gateways-email',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_contato' } },
                component: () => import('@/views/admin/configuracoes/gateways/EmailGateways.vue'),
              },
              {
                path: 'whatsapp',
                name: 'admin-configuracoes-gateways-whatsapp',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_contato' } },
                component: () => import('@/views/admin/configuracoes/gateways/WhatsappGateways.vue'),
              },
              {
                path: 'nfse',
                name: 'admin-configuracoes-gateways-nfse',
                meta: { requiresAuth: true, permissions: { modulo: 'configuracoes', submodulo: 'gateways', permissao: 'can_manage_nfse' } },
                component: () => import('@/views/admin/configuracoes/gateways/NfseGateways.vue'),
              },
            ]
          }
        ]
      },
      {
        path: 'owners',
        name: 'admin-owners',
        component: () => import('../layouts/owners/OwnersLayout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'criar-mae',
            name: 'admin-owners-criar-mae',
            meta: { requiresAuth: true, permissions: { modulo: 'owners', submodulo: 'gestao', permissao: 'can_create_owner_mae' } },
            beforeEnter: CreateMotherOwnerGuard,
            component: () => import('../views/admin/owners/CriarOwnerMaeView.vue'),
          },
          {
            path: 'criar-filho',
            name: 'admin-owners-criar-filho',
            meta: { requiresAuth: true, permissions: { modulo: 'owners', submodulo: 'gestao', permissao: 'can_create_owner_filho' } },
            component: () => import('../views/admin/owners/CriarOwnerFilhoView.vue'),
          },
          {
            path: 'filhos',
            name: 'admin-owners-filhos',
            meta: { requiresAuth: true, permissions: { modulo: 'owners', submodulo: 'gestao', permissao: 'can_create_owner_filho' } },
            component: () => import('../views/admin/owners/ListarFilhosView.vue'),
          },
        ]
      },
      {
        path: 'reembolso',
        name: 'admin-reembolso',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'admin-reembolso-listar',
            meta: { requiresAuth: true, permissions: { modulo: 'financeiro', submodulo: 'reembolsos', permissao: 'can_view' } },
            component: () => import('../views/admin/reembolso/List.vue'),
          },
          {
            path: 'criar',
            name: 'admin-reembolso-criar',
            meta: { requiresAuth: true, permissions: { modulo: 'financeiro', submodulo: 'reembolsos', permissao: 'can_create' } },
            component: () => import('../views/admin/reembolso/Form.vue'),
          }
        ]
      },
      {
        path: 'orcamento',
        name: 'admin-orcamento',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'cliente',
            children: [
              {
                path: '',
                name: 'admin-orcamento-cliente-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'cliente', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/cliente/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-cliente-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'cliente', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/cliente/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-cliente-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'cliente', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/cliente/Form.vue'),
              },
            ],
          },
          {
            path: 'empresa-emissora',
            children: [
              {
                path: '',
                name: 'admin-orcamento-empresa-emissora-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'empresa_emissora', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/empresa-emissora/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-empresa-emissora-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'empresa_emissora', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/empresa-emissora/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-empresa-emissora-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'empresa_emissora', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/empresa-emissora/Form.vue'),
              },
            ],
          },
          {
            path: 'responsavel-assinatura',
            children: [
              {
                path: '',
                name: 'admin-orcamento-responsavel-assinatura-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'responsavel_assinatura', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/responsavel-assinatura/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-responsavel-assinatura-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'responsavel_assinatura', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/responsavel-assinatura/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-responsavel-assinatura-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'responsavel_assinatura', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/responsavel-assinatura/Form.vue'),
              },
            ],
          },
          {
            path: 'configuracoes/modelo-termo',
            children: [
              {
                path: '',
                name: 'admin-orcamento-modelo-termo-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'modelo_termo', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/configuracoes/modelo-termo/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-modelo-termo-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'modelo_termo', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/configuracoes/modelo-termo/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-modelo-termo-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'modelo_termo', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/configuracoes/modelo-termo/Form.vue'),
              },
            ],
          },
          {
            path: 'configuracoes/produto-avulso',
            children: [
              {
                path: '',
                name: 'admin-orcamento-produto-avulso-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'produto_avulso', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/configuracoes/produto-avulso/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-produto-avulso-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'produto_avulso', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/configuracoes/produto-avulso/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-produto-avulso-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'produto_avulso', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/configuracoes/produto-avulso/Form.vue'),
              },
            ],
          },
          {
            path: 'configuracoes/tabela-preco',
            children: [
              {
                path: '',
                name: 'admin-orcamento-tabela-preco-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'tabela_preco', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/configuracoes/tabela-preco/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-tabela-preco-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'tabela_preco', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/configuracoes/tabela-preco/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-orcamento-tabela-preco-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'tabela_preco', permissao: 'can_update' } },
                component: () => import('../views/admin/orcamento/configuracoes/tabela-preco/Form.vue'),
              },
            ],
          },
          {
            path: 'orcamento',
            children: [
              {
                path: '',
                name: 'admin-orcamento-orcamento-listar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'orcamento', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/orcamento/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-orcamento-orcamento-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'orcamento', permissao: 'can_create' } },
                component: () => import('../views/admin/orcamento/orcamento/Form.vue'),
              },
              {
                path: ':uuid',
                name: 'admin-orcamento-orcamento-detalhe',
                meta: { requiresAuth: true, permissions: { modulo: 'orcamento', submodulo: 'orcamento', permissao: 'can_view' } },
                component: () => import('../views/admin/orcamento/orcamento/Detalhe.vue'),
              },
            ],
          },
        ]
      },
      {
        path: 'visita',
        name: 'admin-visita',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'prospecto',
            children: [
              {
                path: '',
                name: 'admin-visita-prospecto-listar',
                meta: { requiresAuth: true, filtrosNaUrl: true, permissions: { modulo: 'visita', submodulo: 'prospecto', permissao: 'can_view' } },
                component: () => import('../views/admin/visita/prospecto/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-visita-prospecto-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'visita', submodulo: 'prospecto', permissao: 'can_create' } },
                component: () => import('../views/admin/visita/prospecto/Form.vue'),
              },
              {
                path: ':uuid/editar',
                name: 'admin-visita-prospecto-editar',
                meta: { requiresAuth: true, permissions: { modulo: 'visita', submodulo: 'prospecto', permissao: 'can_update' } },
                component: () => import('../views/admin/visita/prospecto/Form.vue'),
              },
            ],
          },
          {
            path: 'visita',
            children: [
              {
                path: '',
                name: 'admin-visita-visita-listar',
                meta: { requiresAuth: true, filtrosNaUrl: true, permissions: { modulo: 'visita', submodulo: '_', permissao: 'can_view' } },
                component: () => import('../views/admin/visita/visita/List.vue'),
              },
              {
                path: 'criar',
                name: 'admin-visita-visita-criar',
                meta: { requiresAuth: true, permissions: { modulo: 'visita', submodulo: '_', permissao: 'can_create' } },
                component: () => import('../views/admin/visita/visita/Form.vue'),
              },
              {
                path: ':uuid',
                name: 'admin-visita-visita-detalhe',
                meta: { requiresAuth: true, permissions: { modulo: 'visita', submodulo: '_', permissao: 'can_view' } },
                component: () => import('../views/admin/visita/visita/Detalhe.vue'),
              },
            ],
          },
          {
            path: 'agenda',
            name: 'admin-visita-agenda',
            meta: { requiresAuth: true, filtrosNaUrl: true, permissions: { modulo: 'visita', submodulo: 'agenda', permissao: 'can_view' } },
            component: () => import('../views/admin/visita/visita/Agenda.vue'),
          },
          {
            // Visão do vendedor (contrato visita-relatorios-contract): sempre o vendedor do
            // usuário logado.
            path: 'meu-painel',
            name: 'admin-visita-meu-painel',
            meta: { requiresAuth: true, permissions: { modulo: 'visita', submodulo: '_', permissao: 'can_view' } },
            component: () => import('../views/admin/visita/MeuPainel.vue'),
          },
          {
            // Relatórios de visitas da gestão (contrato visita-relatorios-contract): mesma
            // permissão dos relatórios comerciais, não do módulo visita.
            path: 'relatorios',
            name: 'admin-visita-relatorios',
            meta: { requiresAuth: true, permissions: { modulo: 'relatorio', submodulo: 'comercial', permissao: 'can_view' } },
            component: () => import('../views/admin/visita/relatorio/Index.vue'),
          },
        ]
      },
      {
        path: 'relatorio',
        name: 'admin-relatorio',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'admin-relatorio-listar',
            meta: { requiresAuth: true },
            component: () => import('../views/admin/relatorio/Index.vue'),
          }
        ]
      },
      {
        path: 'usuario',
        name: 'admin-usuario',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'meus-atendimentos',
            name: 'admin-usuario-meus-atendimentos',
            meta: { requiresAuth: true },
            component: () => import('../views/admin/usuario/MeusAtendimentos.vue'),
          },
          // {
          //   path: 'trocar-senha',
          //   name: 'admin-usuario-trocar-senha',
          //   meta: { requiresAuth: true },
          //   component: () => import('../views/admin/usuario/TrocarSenha.vue'),
          // }
        ]
      },
      {
        path: 'fila-videoconferencia',
        name: 'admin-fila-videoconferencia',
        meta: { requiresAuth: true },
        redirect: '/admin/fila-videoconferencia/filas',
        children: [
          {
            path: 'dashboard',
            name: 'admin-fila-videoconferencia-dashboard',
            meta: { requiresAuth: true },
            component: () => import('../views/admin/fila-videoconferencia/DashboardView.vue'),
          },
          {
            path: 'config',
            name: 'admin-fila-videoconferencia-config',
            meta: { requiresAuth: true },
            component: () => import('../views/admin/fila-videoconferencia/Config.vue'),
          }
        ]
      },
      {
        path: 'fila-recuperacao',
        name: 'admin-fila-recuperacao',
        meta: { requiresAuth: true },
        redirect: '/admin/fila-recuperacao/painel',
        children: [
          {
            path: 'dashboard',
            name: 'admin-fila-recuperacao-dashboard',
            meta: { requiresAuth: true, permissions: { modulo: 'fila_recuperacao', submodulo: '_', permissao: 'can_manage' } },
            component: () => import('../views/admin/fila-recuperacao/Dashboard.vue'),
          },
          {
            path: 'painel',
            name: 'admin-fila-recuperacao-painel',
            meta: { requiresAuth: true, permissions: { modulo: 'fila_recuperacao', submodulo: '_', permissao: 'can_operate' } },
            component: () => import('../views/admin/fila-recuperacao/Painel.vue'),
          },
          {
            path: 'configuracoes',
            name: 'admin-fila-recuperacao-configuracoes',
            meta: { requiresAuth: true, permissions: { modulo: 'fila_recuperacao', submodulo: '_', permissao: 'can_manage' } },
            component: () => import('../views/admin/fila-recuperacao/ConfigList.vue'),
          },
          {
            path: 'relatorio',
            name: 'admin-fila-recuperacao-relatorio',
            meta: { requiresAuth: true, permissions: { modulo: 'fila_recuperacao', submodulo: '_', permissao: 'can_manage' } },
            component: () => import('../views/admin/fila-recuperacao/Relatorio.vue'),
          },
        ]
      },
      {
        path: 'suporte-tecnico',
        name: 'admin-suporte-tecnico',
        meta: { requiresAuth: true, permissions: { modulo: 'suporte_tecnico', submodulo: 'solicitacao', permissao: 'can_view' } },
        component: () => import('../views/admin/suporte-tecnico/SolicitacaoList.vue')
      },
      {
        path: 'vendedores',
        name: 'admin-vendedores',
        meta: { requiresAuth: true, permissions: { modulo: 'vendedores', submodulo: '_', permissao: 'can_view' } },
        component: () => import('../views/admin/vendedor/List.vue')
      },
      {
        path: 'portal-agr',
        name: 'admin-portal-agr',
        meta: { requiresAuth: true },
        redirect: '/admin/portal-agr/pages',
        children: [
          {
            path: 'config',
            redirect: '/admin/portal-agr/config/pages',
            name: 'admin-portal-agr-config',
            children:[
              {
                path: 'pages',
                name: 'admin-portal-agr-pages-listar',
                component: () => import('../views/admin/portal_agr/config/List.vue'),
              },
              {
                path: 'pages/:uuid/builder',
                name: 'admin-portal-agr-pages-builder',
                component: () => import('../views/admin/portal_agr/config/Builder.vue'),
              },
            ]
          },
          {
            path: 'view',
            meta: { requiresAuth: true },
            name: 'admin-portal-agr-view',
            component: () => import('../views/admin/portal_agr/public/DisplayPortal.vue'),
          }
        ]
      }
    ],
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    meta: { ambient: 'admin', title: 'Administrativo' },
    beforeEnter: LoginAdminGuard,
    component: () => import('@/views/admin/Login.vue'),
  },
  {
    path: '/admin/sem-acesso',
    name: 'admin-sem-acesso',
    meta: { ambient: 'admin', title: 'Administrativo - Sem acesso' },
    component: () => import('@/views/admin/SemAcesso.vue'),
  },

  // ===== Ecommerce =====
  {
    path: '/',
    redirect: '',
    meta: { isAffiliate: false, ambient: 'ecommerce', title: 'E-commerce' },
    beforeEnter: AuthGuard,
    component: () => import('../layouts/EcommerceLayout.vue'),
    children: [
      {
        path: '',
        name: 'ecommerce-home',
        component: () => import('../views/ecommerce/Home.vue'),
      },
      {
        path: '/certificados',
        name: 'ecommerce-certificados',
        component: () => import('../views/ecommerce/ProductsListPage.vue'),
      },
      {
        path: '/certificados/:slug',
        name: 'ecommerce-certificados-slug',
        component: () => import('../views/ecommerce/ProductPage.vue'),
      },
      {
        path: '/certificados/promo/:slug',
        name: 'ecommerce-certificados-promo',
        component: () => import('../views/ecommerce/PromotionPage.vue'),
      },
      {
        path: '/minhas-compras',
        name: 'ecommerce-minhas-compras',
        meta: { requiresAuth: true },
        component: () => import('../views/ecommerce/MyOrders.vue'),
      },
      {
        path: '/seja-afiliado',
        name: 'seja-afiliado',
        component: () => import('../views/ecommerce/SejaAfiliado.vue'),
      },
      {
        path: '/seja-afiliado/registro',
        name: 'seja-afiliado-registro',
        component: () => import('../views/ecommerce/RegistroAfiliado.vue'),
      },
      {
        path: '/filiais',
        name: 'filiais',
        component: () => import('../views/ecommerce/Filiais.vue'),
      },
      {
        path: '/suporte-tecnico',
        name: 'suporte-tecnico',
        component: () => import('../views/ecommerce/SuporteTecnico.vue'),
      },
    ],
  },
  {
    path: '/checkout',
    name: 'ecommerce-checkout',
    beforeEnter: AuthGuard,
    meta: { requiresAuth: true, isAffiliate: false },
    component: () => import('../views/CheckoutView.vue'),
  },
  {
    path: '/purchase/:uuid',
    name: 'ecommerce-purchase',
    beforeEnter: AuthGuard,
    meta: { requiresAuth: true, isAffiliate: false, ambient: 'ecommerce', title: 'E-commerce' },
    component: () => import('../views/ecommerce/PurchaseView.vue'),
  },
  {
    path: '/validar-biometria',
    name: 'validar-biometria',
    meta: { title: 'Disponibilidade para Videoconferência' },
    component: () => import('../views/ecommerce/ConsultaBiometriaPublicaView.vue'),
  },
  {
    path: '/login',
    name: 'ecommerce-login',
    meta: { isAffiliate: false, ambient: 'ecommerce', title: 'E-commerce' },
    beforeEnter: LoginGuard,
    component: () => import('@/views/ecommerce/Login.vue'),
  },
  {
    path: '/esqueci-senha',
    name: 'ecommerce-forgot-password',
    meta: { isAffiliate: false, ambient: 'ecommerce', title: 'E-commerce' },
    beforeEnter: ForgotPasswordGuard,
    component: () => import('@/views/ecommerce/ForgotPassword.vue'),
  },
  {
    path: '/resetar-senha',
    name: 'ecommerce-reset-password',
    meta: { isAffiliate: false, ambient: 'ecommerce', title: 'E-commerce' },
    beforeEnter: ForgotPasswordGuard,
    component: () => import('@/views/ecommerce/ResetPassword.vue'),
  },

  // ===== Portal do Afiliado =====
  {
    path: '/afiliado',
    redirect: '/afiliado/portal',
    meta: { isAffiliate: true, ambient: 'afiliado-portal', title: 'Portal do Afiliado' },
    children: [
      {
        path: 'portal',
        redirect: '/afiliado/portal/dashboard',
        beforeEnter: AuthAffiliateGuard,
        component: () => import('../layouts/AffiliatePortalLayout.vue'),
        children: [
          {
            path: 'dashboard',
            name: 'affiliate-portal-home',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/PortalDashboard.vue'),
          },
          {
            path: 'compre-indique',
            name: 'affiliate-portal-compre-indique',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliate/portal/PortalCompreIndique.vue'),
          },
          {
            path: 'minhas-indicacoes',
            name: 'affiliate-portal-minhas-indicacoes',
            meta: { requiresAuth: true },
            component: () => import('../views/ecommerce/MyOrders.vue'),
          },
          {
            path: 'meus-dados',
            name: 'affiliate-portal-meus-dados',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliate/portal/PortalMyData.vue'),
          },
          {
            path: 'extrato',
            name: 'affiliate-portal-extrato',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/PortalExtrato.vue'),
          },
          {
            path: 'relatorio',
            name: 'affiliate-portal-relatorio',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/PortalRelatorio.vue'),
          },
          {
            path: 'recuperacao',
            name: 'affiliate-portal-recuperacao',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/Recuperacao.vue'),
          },
          {
            path: 'configuracoes',
            name: 'affiliate-portal-configuracoes',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/ConfiguracoesPortal.vue'),
          },
          {
            path: 'gerenciar-vitrine',
            name: 'affiliate-portal-vitrine',
            meta: { requiresAuth: true },
            component: () => import('../views/affiliatePortal/VitrineManagement.vue'),
          },
        ],
      },
      {
        path: 'login',
        name: 'affiliate-portal-login',
        beforeEnter: LoginAffiliateGuard,
        component: () => import('@/views/affiliate/portal/PortalLogin.vue'),
      }
    ]
  },

  // ===== Loja do Afiliado =====
  {
    path: '/afiliado/loja/:code',
    redirect: '',
    meta: { isAffiliate: true, ambient: 'afiliado-loja', title: 'Loja do Afiliado' },
    beforeEnter: LojaAffiliateGuard,
    component: () => import('@/layouts/AffiliateLojaLayout.vue'),
    children: [
      {
        path: '',
        name: 'afiliado-loja',
        component: () => import('@/views/affiliate/loja/HomeView.vue'),
      },
      {
        path: 'certificados/:slug',
        name: 'afiliado-loja-certificados-slug',
        component: () => import('../views/ecommerce/ProductPage.vue'),
      },
      {
        path: 'certificados/promo/:slug',
        name: 'afiliado-loja-certificados-promo',
        component: () => import('../views/ecommerce/PromotionPage.vue'),
      },
      {
        path: 'minhas-compras',
        name: 'afiliado-loja-minhas-compras',
        meta: { requiresAuth: true },
        component: () => import('../views/ecommerce/MyOrders.vue'),
      },
      {
        path: 'suporte-tecnico',
        name: 'afiliado-loja-suporte-tecnico',
        meta: { requiresAuth: false },
        component: () => import('../views/ecommerce/SuporteTecnico.vue'),
      }
    ]
  },
  {
    path: '/afiliado/loja/:code/login',
    name: 'affiliate-loja-login',
    meta: { ambient: 'afiliado-loja', isAffiliate: true, title: 'Loja do Afiliado' },
    component: () => import('@/views/ecommerce/Login.vue'),
  },
  {
    path: '/afiliado/loja/:code/checkout',
    name: 'afiliado-loja-checkout',
    meta: { requiresAuth: true, isAffiliate: true, ambient: 'afiliado-loja', title: 'Loja do Afiliado' },
    beforeEnter: LojaAffiliateGuard,
    component: () => import('../views/CheckoutView.vue'),
  },
  {
    path: '/afiliado/loja/:code/purchase/:uuid',
    name: 'afiliado-loja-purchase',
    meta: { requiresAuth: true, isAffiliate: true, ambient: 'afiliado-loja', title: 'Loja do Afiliado' },
    beforeEnter: LojaAffiliateGuard,
    component: () => import('../views/ecommerce/PurchaseView.vue'),
  },

  // ===== Outras Rotas =====
  {
    path: '/pagamento/:uuid',
    name: 'payment-view',
    component: () => import('@/views/admin/PaymentView.vue'),
  },
  {
    path: '/link-pagamento/:uuid',
    name: 'payment-link-view',
    component: () => import('@/views/admin/PaymentLinkView.vue'),
  },
  {
    path: '/:pathMatch(.*)',
    component: () => import('@/views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
})

router.beforeEach((to, from, next) => {
  const authAdminStore = useAuthAdminStore()

  document.title = to.meta.title || 'Partner Center'

  if (to.meta.requiresAuth && authAdminStore.isAuthenticated && to.meta.permissions) {
    const { modulo, submodulo, permissao } = to.meta.permissions
    const permitido = authAdminStore.hasPermission(modulo, submodulo, permissao)
    if (!permitido) {
      return next('/admin/sem-acesso')
    }
  }
  next()
})

export default router
